import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useDashboardMapaCalor from './useDashboardMapaCalor';

const DEFAULT_PAGE_SIZE = 50;

const normalizeColumns = (dados) =>
  [...(dados?.dimensoes || [])]
    .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0))
    .map((dimension, index) => ({
      id: String(dimension.id ?? index),
      label: dimension.titulo || '',
    }));

const getAreaKey = (areaId) => String(areaId);

const mergeAreas = (currentAreas, newAreas) => {
  const areasById = new Map(
    currentAreas.map((area) => [getAreaKey(area.areaId), area])
  );

  newAreas.forEach((area) => areasById.set(getAreaKey(area.areaId), area));

  return Array.from(areasById.values());
};

export default function useHierarquiaMapaCalor(
  entidadeId,
  { endpoint, enabled = true, filtros, normalizeArea, refreshToken = 0 }
) {
  const [rootPage, setRootPage] = useState(0);
  const [consolidaAreasSubordinadas, setConsolidaAreasSubordinadas] =
    useState(true);
  const [expandedAreaIds, setExpandedAreaIds] = useState([]);
  const [childrenByAreaId, setChildrenByAreaId] = useState({});
  const cacheVersionRef = useRef(0);
  const { carregarNivel, dados, erro, isLoading, retry } =
    useDashboardMapaCalor(endpoint, entidadeId, {
      consolidaAreasSubordinadas,
      enabled,
      filtros,
      page: rootPage,
      refreshToken,
    });

  const clearTreeCache = useCallback(() => {
    cacheVersionRef.current += 1;
    setExpandedAreaIds([]);
    setChildrenByAreaId({});
  }, []);

  useEffect(() => {
    setRootPage(0);
    clearTreeCache();
  }, [clearTreeCache, endpoint, entidadeId, filtros, refreshToken]);

  const columns = useMemo(() => normalizeColumns(dados), [dados]);
  const rootRows = useMemo(
    () => (dados?.areas || []).map(normalizeArea),
    [dados, normalizeArea]
  );

  const loadChildren = useCallback(
    async (parentAreaId, page = 0, append = false) => {
      const parentKey = getAreaKey(parentAreaId);
      const requestCacheVersion = cacheVersionRef.current;

      setChildrenByAreaId((current) => ({
        ...current,
        [parentKey]: {
          areas: [],
          loaded: false,
          page: -1,
          pageSize: DEFAULT_PAGE_SIZE,
          totalAreas: 0,
          ...current[parentKey],
          isLoading: true,
        },
      }));

      try {
        const response = await carregarNivel({ areaPaiId: parentAreaId, page });

        if (requestCacheVersion !== cacheVersionRef.current) return;

        if (getAreaKey(response?.areaPaiId) !== parentKey) {
          setChildrenByAreaId((current) => ({
            ...current,
            [parentKey]: {
              ...current[parentKey],
              isLoading: false,
            },
          }));
          return;
        }

        const responseAreas = response?.areas || [];
        const totalAreas = Number(response?.totalAreas || 0);

        setChildrenByAreaId((current) => {
          const currentBlock = current[parentKey] || {};

          return {
            ...current,
            [parentKey]: {
              areas: append
                ? mergeAreas(currentBlock.areas || [], responseAreas)
                : responseAreas,
              isLoading: false,
              loaded: true,
              page: Number(response?.pagina ?? page),
              pageSize: Number(response?.tamanhoPagina || DEFAULT_PAGE_SIZE),
              totalAreas,
            },
          };
        });

        if (totalAreas === 0) {
          setExpandedAreaIds((current) =>
            current.filter((areaId) => areaId !== parentKey)
          );
        }
      } catch {
        if (requestCacheVersion !== cacheVersionRef.current) return;

        setChildrenByAreaId((current) => ({
          ...current,
          [parentKey]: {
            ...current[parentKey],
            isLoading: false,
          },
        }));
        setExpandedAreaIds((current) =>
          current.filter((areaId) => areaId !== parentKey)
        );
      }
    },
    [carregarNivel]
  );

  const toggleRow = useCallback(
    (row) => {
      const areaKey = getAreaKey(row.id);
      const isExpanded = expandedAreaIds.includes(areaKey);
      const childrenBlock = childrenByAreaId[areaKey];

      setExpandedAreaIds((current) =>
        isExpanded
          ? current.filter((areaId) => areaId !== areaKey)
          : [...current, areaKey]
      );

      if (!isExpanded && !childrenBlock?.loaded && !childrenBlock?.isLoading) {
        loadChildren(row.id);
      }
    },
    [childrenByAreaId, expandedAreaIds, loadChildren]
  );

  const rows = useMemo(() => {
    const visibleRows = [];
    const expandedIds = new Set(expandedAreaIds);

    const appendRow = (row) => {
      const areaKey = getAreaKey(row.id);
      const childrenBlock = childrenByAreaId[areaKey];
      const isExpanded = expandedIds.has(areaKey);
      const hasChildren =
        row.hasChildren &&
        !(childrenBlock?.loaded && childrenBlock.totalAreas === 0);

      visibleRows.push({
        ...row,
        hasChildren,
        isExpanded,
        isLoading:
          isExpanded &&
          childrenBlock?.isLoading === true &&
          !childrenBlock.loaded,
      });

      if (!isExpanded || !childrenBlock?.loaded) return;

      childrenBlock.areas
        .map(normalizeArea)
        .forEach((childRow) => appendRow(childRow));

      if (childrenBlock.areas.length < childrenBlock.totalAreas) {
        visibleRows.push({
          id: `__load-more__${areaKey}`,
          isCustom: true,
          isLoading: childrenBlock.isLoading,
          level: row.level + 1,
          parentAreaId: row.id,
        });
      }
    };

    rootRows.forEach((row) => appendRow(row));

    return visibleRows;
  }, [childrenByAreaId, expandedAreaIds, normalizeArea, rootRows]);

  const loadMoreChildren = useCallback(
    (row) => {
      const childrenBlock = childrenByAreaId[getAreaKey(row.parentAreaId)];
      if (!childrenBlock || childrenBlock.isLoading) return;

      loadChildren(row.parentAreaId, childrenBlock.page + 1, true);
    },
    [childrenByAreaId, loadChildren]
  );

  const changeRootPage = useCallback(
    (page) => {
      clearTreeCache();
      setRootPage(page - 1);
    },
    [clearTreeCache]
  );

  const retryRoot = useCallback(() => {
    clearTreeCache();
    retry();
  }, [clearTreeCache, retry]);

  const changeConsolidation = useCallback(
    (checked) => {
      clearTreeCache();
      setRootPage(0);
      setConsolidaAreasSubordinadas(checked);
    },
    [clearTreeCache]
  );

  return {
    changeConsolidation,
    changeRootPage,
    columns,
    consolidaAreasSubordinadas,
    erro,
    isLoading,
    loadMoreChildren,
    paginationCount: Number(dados?.totalAreas || 0),
    paginationCurrentPage: Number(dados?.pagina ?? rootPage) + 1,
    paginationPageSize: Number(dados?.tamanhoPagina || DEFAULT_PAGE_SIZE),
    retryRoot,
    rows,
    toggleRow,
  };
}
